import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, MessageSquarePlus, Send, Briefcase, Loader2, ArrowLeft, RefreshCw, X, Search, Crown, Trash2 } from "lucide-react";
import { API, authHeaders, ROLE_BADGE_STYLES } from "@/constants/roles";
import { useChatUploads, useChatDropPaste, AttachButton, PendingAttachments, AttachmentList, DragOverlay } from "@/components/chat/Attachments";

const fmtTime = (iso) =>
  new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export const AdminMessages = ({ onRead, canDelete = false, viewerRole = "admin" }) => {
  const [convs, setConvs] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [openConv, setOpenConv] = useState("owner");
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [jobId, setJobId] = useState("");
  const uploads = useChatUploads();
  const { dragging, dragProps, onPaste } = useChatDropPaste(uploads.addFiles);
  const [sending, setSending] = useState(false);
  const [contacts, setContacts] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [contactQuery, setContactQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const bottomRef = useRef(null);

  const loadContacts = useCallback(() => {
    axios.get(`${API}/admin/messages/contacts`, { headers: authHeaders() }).then((r) => setContacts(r.data)).catch(() => {});
  }, []);

  const loadConvs = useCallback((silent) => {
    axios
      .get(`${API}/admin/messages`, { headers: authHeaders() })
      .then((r) => setConvs(r.data))
      .catch(() => {
        if (!silent) toast.error("Could not load conversations.");
      });
  }, []);

  const loadThread = useCallback(
    (wid, conv, silent) => {
      axios
        .get(`${API}/admin/messages/${wid}?conversation=${conv}`, { headers: authHeaders() })
        .then((r) => {
          setThread(r.data);
          onRead?.();
          loadConvs(true);
        })
        .catch((err) => {
          if (!silent) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not load conversation.");
          }
        });
    },
    [onRead, loadConvs]
  );

  useEffect(() => {
    loadConvs();
    loadContacts();
    const t = setInterval(() => loadConvs(true), 15000);
    return () => clearInterval(t);
  }, [loadConvs, loadContacts]);

  useEffect(() => {
    if (!openId) return;
    loadThread(openId, openConv);
    const t = setInterval(() => loadThread(openId, openConv, true), 15000);
    return () => clearInterval(t);
  }, [openId, openConv, loadThread]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [thread?.messages?.length]);

  // default conversation for a contact: worker targets open the owner's private line for the
  // owner, the shared PM Team inbox for managers; staff targets are always a single DM thread.
  const openThread = (wid, conv) => {
    setOpenId(wid);
    setOpenConv(conv || (viewerRole === "admin" ? "owner" : "pm_team"));
    setThread(null);
    setText("");
    setJobId("");
    uploads.clear();
  };

  const send = async (e) => {
    e.preventDefault();
    if ((!text.trim() && uploads.metas.length === 0) || !openId || uploads.uploading || uploads.failed) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/messages/${openId}`, { text: text.trim(), job_id: jobId || null, conversation: openConv, attachments: uploads.metas }, { headers: authHeaders() });
      setText("");
      setJobId("");
      uploads.clear();
      loadThread(openId, openConv, true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Message failed to send.");
    } finally {
      setSending(false);
    }
  };

  const linkedJob = thread?.jobs?.find((j) => j.id === jobId);

  // Owner-only single-message delete. Preview/unread/order are derived server-side, so
  // after removal we just drop it locally + refresh the conversation list and badge.
  const confirmDelete = async () => {
    if (!deleteTarget || !openId || deleting) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/messages/${openId}/${deleteTarget.id}`, { headers: authHeaders() });
      setThread((t) => (t ? { ...t, messages: t.messages.filter((x) => x.id !== deleteTarget.id) } : t));
      setDeleteTarget(null);
      toast.success("Message deleted for everyone.");
      loadConvs(true);
      onRead?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not delete the message.");
    } finally {
      setDeleting(false);
    }
  };

  const filteredContacts = (contacts || []).filter((p) => {
    const q = contactQuery.trim().toLowerCase();
    if (!q) return true;
    return ((p.name || "") + " " + p.email + " " + (p.worker_role || "")).toLowerCase().includes(q);
  });

  const contactPicker = showPicker && (
    <div data-testid="new-chat-picker" className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setShowPicker(false)}>
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[var(--pv-border)] px-5 py-4">
          <div>
            <p className="font-display text-sm font-bold text-[var(--pv-text)]">Start a conversation</p>
            <p className="text-[11px] text-[var(--pv-text-4)]">Any animator, manager or the owner — no first message needed from them.</p>
          </div>
          <button data-testid="new-chat-close" onClick={() => setShowPicker(false)} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="border-b border-[var(--pv-border)] px-5 py-3">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--pv-text-4)]" />
            <input
              data-testid="new-chat-search"
              autoFocus
              value={contactQuery}
              onChange={(e) => setContactQuery(e.target.value)}
              placeholder="Search name, email or role..."
              className="w-full rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] py-2.5 pl-9 pr-4 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none"
            />
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {!contacts ? (
            <p className="py-12 text-center text-sm text-[var(--pv-text-4)]">Loading...</p>
          ) : filteredContacts.length === 0 ? (
            <p data-testid="new-chat-empty" className="py-12 text-center text-sm text-[var(--pv-text-4)]">No one matches that search.</p>
          ) : (
            filteredContacts.map((p) => (
              <button
                key={p.id}
                data-testid={`new-chat-contact-${p.id}`}
                onClick={() => { setShowPicker(false); setContactQuery(""); openThread(p.id, p.account_role === "worker" ? undefined : "owner"); }}
                className="flex w-full items-center gap-3 border-b border-[var(--pv-border)] px-5 py-3.5 text-left transition-colors last:border-0 hover:bg-[var(--pv-subtle)]"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-500/15 font-display text-xs font-bold text-cyan-300">
                  {(p.name || p.email).slice(0, 1).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-bold text-[var(--pv-text)]">{p.name || p.email}</span>
                    {p.is_top_animator && <Crown className="h-3 w-3 shrink-0 text-amber-400" />}
                  </span>
                  <span className="block truncate text-[11px] text-[var(--pv-text-4)]">{p.email}</span>
                </span>
                {p.worker_role && (
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${ROLE_BADGE_STYLES[p.worker_role] || "border-[var(--pv-border)] text-[var(--pv-text-3)]"}`}>
                    {p.worker_role}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );

  const conversationList = (
    <div className={`${openId ? "hidden md:flex" : "flex"} w-full flex-col md:w-80 md:shrink-0 md:border-r md:border-[var(--pv-border)]`}>
      <div className="flex items-center justify-between border-b border-[var(--pv-border)] px-5 py-4">
        <p className="font-display text-sm font-bold text-[var(--pv-text)]">Conversations</p>
        <div className="flex items-center gap-1">
          <button data-testid="new-chat-btn" onClick={() => setShowPicker(true)} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-3 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-cyan-600" aria-label="Start a new conversation">
            <MessageSquarePlus className="h-3.5 w-3.5" /> New chat
          </button>
          <button data-testid="messages-refresh-btn" onClick={() => loadConvs()} className="p-1.5 text-[var(--pv-text-4)] transition-colors hover:text-[var(--pv-text)]" aria-label="Refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {!convs ? (
          <p data-testid="conversations-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading...</p>
        ) : convs.length === 0 ? (
          <div data-testid="conversations-empty" className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <MessageSquare className="h-10 w-10 text-[var(--pv-text-4)]" />
            <p className="text-sm text-[var(--pv-text-4)]">No conversations yet — hit "New chat" to message anyone on the team.</p>
          </div>
        ) : (
          convs.map((c) => (
            <button
              key={`${c.worker_id}:${c.conversation}`}
              data-testid={`conversation-${c.worker_id}-${c.conversation}`}
              onClick={() => openThread(c.worker_id, c.conversation)}
              className={`flex w-full items-start gap-3 border-b border-[var(--pv-border)] px-5 py-4 text-left transition-colors hover:bg-[var(--pv-subtle)] ${openId === c.worker_id && openConv === c.conversation ? "bg-[var(--pv-subtle)]" : ""}`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-500/15 font-display text-xs font-bold text-cyan-300">
                {(c.worker_name || c.worker_email || "?").slice(0, 1).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-bold text-[var(--pv-text)]">{c.worker_name || c.worker_email}</span>
                  <span className="shrink-0 text-[10px] text-[var(--pv-text-4)]">{timeAgo(c.last_at)}</span>
                </span>
                {!c.is_staff_thread && (
                  <span className={`mt-0.5 inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${c.conversation === "pm_team" ? "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>
                    {c.conversation === "pm_team" ? "PM Team" : "Owner"}
                  </span>
                )}
                <span className="mt-0.5 block truncate text-xs text-[var(--pv-text-3)]">
                  {c.last_sender === "admin" ? `${c.last_sender_name || "Team"}: ` : ""}{c.last_message}
                </span>
                {c.last_job_title && (
                  <span className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-cyan-400">
                    <Briefcase className="h-3 w-3" /> {c.last_job_title}
                  </span>
                )}
              </span>
              {c.unread > 0 && (
                <span data-testid={`conversation-unread-${c.worker_id}-${c.conversation}`} className="mt-1 flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
                  {c.unread}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );

  const threadView = openId && (
    <div className={`${openId ? "flex" : "hidden md:flex"} min-w-0 flex-1 flex-col`}>
      {!thread ? (
        <p data-testid="thread-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading conversation...</p>
      ) : (
        <>
          <div className="flex items-center gap-3 border-b border-[var(--pv-border)] px-5 py-4">
            <button data-testid="thread-back-btn" onClick={() => setOpenId(null)} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)] md:hidden" aria-label="Back">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-500/15 font-display text-xs font-bold text-cyan-300">
              {(thread.worker.name || thread.worker.email).slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-sm font-bold text-[var(--pv-text)]">
                <span className="truncate">{thread.worker.name || thread.worker.email}</span>
                {thread.worker.is_top_animator && (
                  <span data-testid="thread-top-animator-badge" className="flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold text-amber-300">
                    <Crown className="h-3 w-3" /> Top Animator
                  </span>
                )}
                {thread.worker.worker_role && (
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${ROLE_BADGE_STYLES[thread.worker.worker_role] || ROLE_BADGE_STYLES["General / Multiple Roles"]}`}>
                    {thread.worker.worker_role}
                  </span>
                )}
              </p>
              <p className="truncate text-[11px] text-[var(--pv-text-4)]">
                {thread.conversation === "pm_team"
                  ? "Production Manager Team — shared inbox, every reply shows its sender"
                  : thread.is_staff_thread
                    ? thread.worker.email
                    : "Private owner conversation"}
              </p>
            </div>
          </div>

          <div {...dragProps} className="relative flex-1 space-y-3 overflow-y-auto px-4 py-4 sm:px-6">
            <DragOverlay visible={dragging} />
            {thread.messages.length === 0 && (
              <p data-testid="thread-empty" className="py-14 text-center text-sm text-[var(--pv-text-4)]">
                No messages yet — start the conversation with {thread.worker.name || "them"}.
              </p>
            )}
            {thread.messages.map((m) => {
              // own staff messages (or legacy staff messages without attribution) go right;
              // the other party + other staff members go left with their name
              const own = m.sender === "admin" && (!m.sender_email || m.sender_email === thread.you);
              const roleTag = m.sender_role === "manager" ? " — Production Manager" : m.sender_role === "admin" ? " — Owner" : "";
              const senderLabel = m.sender === "worker"
                ? (thread.worker.name || "Worker")
                : own ? null : `${m.sender_name || "Team"}${roleTag}`;
              return (
              <div key={m.id} data-testid={`admin-message-${m.id}`} className={`group flex items-center gap-1.5 ${own ? "justify-end" : "justify-start"}`}>
                {canDelete && own && (
                  <button
                    data-testid={`message-delete-${m.id}`}
                    onClick={() => setDeleteTarget(m)}
                    aria-label="Delete message"
                    className="shrink-0 rounded-full p-1.5 text-[var(--pv-text-4)] opacity-0 transition-opacity hover:bg-rose-500/15 hover:text-rose-400 focus:opacity-100 group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${own ? "bg-cyan-500 text-white" : "bg-[var(--pv-raised)] text-[var(--pv-text)]"}`}>
                  {m.job_title && (
                    <p className={`mb-1 flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${own ? "bg-white/20 text-white" : "bg-cyan-500/15 text-cyan-300"}`}>
                      <Briefcase className="h-3 w-3" /> {m.job_title}
                    </p>
                  )}
                  <AttachmentList attachments={m.attachments} mine={own} />
                  {m.text ? <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.text}</p> : null}
                  <p className={`mt-1 text-right text-[10px] ${own ? "text-cyan-100" : "text-[var(--pv-text-4)]"}`}>
                    {senderLabel ? `${senderLabel} · ` : ""}{fmtTime(m.created_at)}
                  </p>
                </div>
                {canDelete && !own && (
                  <button
                    data-testid={`message-delete-${m.id}`}
                    onClick={() => setDeleteTarget(m)}
                    aria-label="Delete message"
                    className="shrink-0 rounded-full p-1.5 text-[var(--pv-text-4)] opacity-0 transition-opacity hover:bg-rose-500/15 hover:text-rose-400 focus:opacity-100 group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          <form data-testid="admin-message-form" onSubmit={send} className="border-t border-[var(--pv-border)] p-4">
            <PendingAttachments pending={uploads.pending} onRetry={uploads.retry} onRemove={uploads.remove} />
            {linkedJob && (
              <div className="mb-2 flex items-center gap-2">
                <span className="flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/15 px-3 py-1 text-[11px] font-bold text-cyan-300">
                  <Briefcase className="h-3 w-3" /> {linkedJob.title}
                  <button type="button" data-testid="admin-unlink-job-btn" onClick={() => setJobId("")} className="text-cyan-300 hover:text-white" aria-label="Remove job link">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <select
                data-testid="admin-message-job-select"
                value={jobId}
                onChange={(e) => setJobId(e.target.value)}
                className="w-44 shrink-0 rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3 py-2.5 text-xs font-semibold text-[var(--pv-text-3)] focus:border-cyan-400 focus:outline-none"
              >
                <option value="">No job linked</option>
                {thread.jobs.map((j) => (
                  <option key={j.id} value={j.id}>{j.title}</option>
                ))}
              </select>
              <input
                data-testid="admin-message-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onPaste={onPaste}
                placeholder={`Message ${thread.worker.name || "them"}...`}
                maxLength={2000}
                className="min-w-0 flex-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10"
              />
              <AttachButton onFiles={uploads.addFiles} disabled={sending} />
              <button
                data-testid="admin-message-send-btn"
                type="submit"
                disabled={sending || uploads.uploading || uploads.failed || (!text.trim() && uploads.metas.length === 0)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan-500 text-white shadow-md shadow-cyan-500/25 transition-colors hover:bg-cyan-600 disabled:opacity-50"
                aria-label="Send reply"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );

  return (
    <div data-testid="admin-messages">
      <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Messages</h1>
      <p className="mt-1 text-sm text-[var(--pv-text-4)]">Direct messages with anyone on the team — start a conversation anytime, no Discord needed.</p>
      <div className="mt-6 flex h-[70vh] overflow-hidden rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)]">
        {conversationList}
        {openId ? threadView : (
          <div className="hidden flex-1 items-center justify-center md:flex">
            <p className="text-sm text-[var(--pv-text-4)]">Select a conversation — or start a new one with anyone on the team.</p>
          </div>
        )}
      </div>
      {contactPicker}
      {deleteTarget && (
        <div data-testid="message-delete-modal" className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="w-full max-w-sm rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-500/15"><Trash2 className="h-4 w-4 text-rose-400" /></span>
              <h2 className="font-display text-base font-bold text-[var(--pv-text)]">Delete this message?</h2>
            </div>
            <p className="mt-3 line-clamp-3 rounded-2xl bg-[var(--pv-subtle)] px-3.5 py-2.5 text-xs text-[var(--pv-text-3)]" data-testid="message-delete-preview">
              "{deleteTarget.text.length > 160 ? deleteTarget.text.slice(0, 160) + "…" : deleteTarget.text}"
            </p>
            <p className="mt-3 text-xs text-[var(--pv-text-4)]">It's removed from the conversation for everyone — this can't be undone.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="message-delete-cancel-btn" onClick={() => setDeleteTarget(null)} disabled={deleting} className="rounded-full border border-[var(--pv-border)] px-4 py-2 text-xs font-bold text-[var(--pv-text-3)] transition-colors hover:bg-[var(--pv-subtle)] disabled:opacity-60">
                Cancel
              </button>
              <button data-testid="message-delete-confirm-btn" onClick={confirmDelete} disabled={deleting} className="flex items-center gap-1.5 rounded-full bg-rose-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-rose-600 disabled:opacity-60">
                {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
